import React, { useState, useRef, useEffect } from "react";

const SliderControl = ({
  value,
  onChange,
  segments,
  activeColor,
  label,
  sliderColor,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const sliderRef = useRef(null);

  const getCurrentSegment = (value, segments) => {
    return segments.find(
      (segment) => value >= segment.min && value <= segment.max
    );
  };

  const handleSliderMove = (clientX) => {
    if (!sliderRef.current || !isDragging) return;

    const slider = sliderRef.current;
    const rect = slider.getBoundingClientRect();
    const x = clientX - rect.left;
    const percentage = Math.min(Math.max((x / rect.width) * 100, 0), 100);

    const step = 20;
    const snappedValue = Math.round(percentage / step) * step;
    onChange(snappedValue);
  };

  const createSegmentedBackground = (value, segments, activeColor) => {
    const totalSegments = segments.length;
    let background = [];

    segments.forEach((segment, index) => {
      const segmentStart = (index / totalSegments) * 100;
      const segmentEnd = ((index + 1) / totalSegments) * 100;

      if (value >= segment.max) {
        background.push(
          `${activeColor} ${segment.opacity}) ${segmentStart}% ${segmentEnd}%`
        );
      } else if (value >= segment.min && value <= segment.max) {
        const percentage = (value - segment.min) / (segment.max - segment.min);
        const gradientEnd =
          segmentStart + (segmentEnd - segmentStart) * percentage;
        background.push(
          `${activeColor} ${segment.opacity}) ${segmentStart}% ${gradientEnd}%`
        );
        background.push(
          `rgba(200, 200, 200, 0.4) ${gradientEnd}% ${segmentEnd}%`
        );
      } else {
        background.push(
          `rgba(200, 200, 200, 0.4) ${segmentStart}% ${segmentEnd}%`
        );
      }
    });

    return `linear-gradient(90deg, ${background.join(", ")})`;
  };

  const handleMouseMove = (e) => handleSliderMove(e.clientX);
  const handleTouchMove = (e) => handleSliderMove(e.touches[0].clientX);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", () => setIsDragging(false));
      document.addEventListener("touchmove", handleTouchMove);
      document.addEventListener("touchend", () => setIsDragging(false));
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", () => setIsDragging(false));
      document.removeEventListener("touchmove", handleTouchMove);
      document.removeEventListener("touchend", () => setIsDragging(false));
    };
  }, [isDragging]);

  return (
    <div className="flex flex-col items-start w-full">
      <label className="text-[20px] leading-[28px] font-normal font-young-serif text-[#3A1A1A] pb-5">
        {label}
      </label>
      <div
        className="relative flex items-center w-full"
        ref={sliderRef}
        onMouseDown={() => setIsDragging(true)}
        onTouchStart={() => setIsDragging(true)}
      >
        <div
          className="absolute left-0 top-1/2 transform -translate-y-1/2 h-5 w-full rounded-full"
          style={{
            background: createSegmentedBackground(value, segments, activeColor),
          }}
        />
        <input
          type="range"
          min="0"
          max="100"
          step="20"
          value={value}
          onChange={(e) => onChange(parseInt(e.target.value))}
          className="relative appearance-none w-full h-4 bg-transparent outline-none"
          style={{ WebkitAppearance: 'none', appearance: 'none' }}
        />
        <div
          className="absolute top-1/2 transform -translate-y-1/2 -translate-x-1/2 flex items-center justify-center h-14 w-14 rounded-full shadow-[4px_6px_12px_rgba(0,0,0,0.5)] cursor-pointer"
          style={{
            left: `${value}%`,
            backgroundColor: sliderColor,
          }}
        >
          <span className="text-[33px]">
            {getCurrentSegment(value, segments).icon}
          </span>
        </div>
      </div>
    </div>
  );
};

const FamjamVibeSlider = ({ onBack, onComplete }) => {
  const [funValue, setFunValue] = useState(60);
  const [bondingValue, setBondingValue] = useState(60);
  const [learningValue, setLearningValue] = useState(60);

  const funSegments = [
    { min: 0, max: 20, opacity: 0.2, icon: "🤔" },
    { min: 20, max: 40, opacity: 0.4, icon: "🧐" },
    { min: 40, max: 60, opacity: 0.6, icon: "🧠" },
    { min: 60, max: 80, opacity: 0.8, icon: "🧑‍🔬" },
    { min: 80, max: 100, opacity: 1, icon: "🧙‍♀️" },
  ];

  const bondingSegments = [
    { min: 0, max: 20, opacity: 0.2, icon: "🖇️" },
    { min: 20, max: 40, opacity: 0.4, icon: "🪇" },
    { min: 40, max: 60, opacity: 0.6, icon: "🫶" },
    { min: 60, max: 80, opacity: 0.8, icon: "❤️" },
    { min: 80, max: 100, opacity: 1, icon: "❤️‍🔥" },
  ];

  const learningSegments = [
    { min: 0, max: 20, opacity: 0.2, icon: "🙂" },
    { min: 20, max: 40, opacity: 0.4, icon: "😄" },
    { min: 40, max: 60, opacity: 0.6, icon: "🥳" },
    { min: 60, max: 80, opacity: 0.8, icon: "🤩" },
    { min: 80, max: 100, opacity: 1, icon: "😱" },
  ];

  return (
    <div className="flex flex-col items-center pt-[26px] pr-[10px] pb-[11px] pl-[17px] max-w-sm mx-auto bg-white min-h-screen">
      <div className="w-full flex items-center justify-between">
        <button
          onClick={onBack}
          className="bg-white rounded-full p-1 shadow-[2px_2px_4px_rgba(0,0,0,0.05),_5px_5px_2px_rgba(0,0,0,0.15)]"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6 text-gray-400"
            fill="none"
            viewBox="0 0 26 26"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
        </button>
        <h1 className="text-[30px] leading-[33px] font-normal font-young-serif text-[#3A1A1A] pt-8 text-center">
          Set your
          <p /> Famjam vibe?
        </h1>
        <div className="w-6"></div>
      </div>

      <div className="w-full flex flex-col gap-16 mt-14 px-6">
        <SliderControl
          value={funValue}
          onChange={setFunValue}
          segments={funSegments}
          activeColor="rgba(254, 163, 117,"
          sliderColor="#FEA375"
          label="Fun"
        />

        <SliderControl
          value={bondingValue}
          onChange={setBondingValue}
          segments={bondingSegments}
          activeColor="rgba(246, 179, 246,"
          sliderColor="#F6B3F6"
          label="Bonding"
        />

        <SliderControl
          value={learningValue}
          onChange={setLearningValue}
          segments={learningSegments}
          activeColor="rgba(96, 183, 255,"
          sliderColor="#60B7FF"
          label="Learning"
        />
      </div>

      <p className="text-center text-[16px] font-normal pt-[90px] px-4 font-space-grotesk text-[#1E0A1E]">
        Every day we'll share a <span className="font-bold">Famjam</span> of
        activities based on these slides. You can tweak them in the hub.
      </p>

      <img
        src="/images/user-group.png"
        alt="family"
        className="rounded-full object-cover mb-8 mt-3"
      />

      <div className="flex justify-center mt-9 mb-3">
        <button
          onClick={onComplete}
          className="font-space-grotesk bg-[#1E0A1E] text-[#EEEEEE] text-[16px] font-medium px-5 py-2.5 rounded-full hover:bg-gray-800 shadow-[2px_2px_4px_rgba(0,0,0,0.05),_5px_5px_2px_rgba(0,0,0,0.15)]"
        >
          Save
        </button>
      </div>
    </div>
  );
};

export default FamjamVibeSlider;